import { en } from './en';
import { ru } from './ru';
import type { Dictionary, Locale, TranslationKey } from './types';

export type { Locale, TranslationKey } from './types';

const STORAGE_KEY = 'gaz.locale';
const dictionaries: Record<Locale, Dictionary> = { ru, en };

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'en') return saved;
  } catch {
    /* storage unavailable */
  }
  return 'ru';
}

let current: Locale = initialLocale();

export function getLocale(): Locale {
  return current;
}

export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  let text: string = dictionaries[current][key] ?? ru[key] ?? key;
  if (params) {
    for (const [name, value] of Object.entries(params)) text = text.replaceAll(`{${name}}`, String(value));
  }
  return text;
}

export function setLocale(locale: Locale): void {
  if (locale === current) return;
  current = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  document.documentElement.lang = locale;
  applyTranslations(document);
  document.dispatchEvent(new CustomEvent('i18n:changed', { detail: locale }));
}

/** Fills elements marked with data-i18n / -placeholder / -title / -aria-label. */
export function applyTranslations(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    el.textContent = t(el.dataset.i18n as TranslationKey);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach((el) => {
    el.setAttribute('placeholder', t(el.dataset.i18nPlaceholder as TranslationKey));
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
    el.setAttribute('title', t(el.dataset.i18nTitle as TranslationKey));
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-aria-label]').forEach((el) => {
    el.setAttribute('aria-label', t(el.dataset.i18nAriaLabel as TranslationKey));
  });
}

/** forms = "one|few|many" (RU rules) or "one|other|other" for EN. */
export function pluralize(count: number, forms: string): string {
  const [one = '', few = one, many = few] = forms.split('|');
  if (current === 'en') return count === 1 ? one : few;
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return many;
  if (n1 > 1 && n1 < 5) return few;
  if (n1 === 1) return one;
  return many;
}

/** Picks `<field>_en` for EN locale when filled, falls back to `<field>_ru`. */
export function loc(row: Record<string, unknown>, field: string): string {
  const ruValue = row[`${field}_ru`];
  const enValue = row[`${field}_en`];
  if (current === 'en' && typeof enValue === 'string' && enValue.trim()) return enValue;
  return typeof ruValue === 'string' ? ruValue : '';
}

export function formatDate(value: string | null | undefined, withTime = false): string {
  if (!value) return '';
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(current === 'ru' ? 'ru-RU' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}
